import {
  Box,
  Button,
  Checkbox,
  HStack,
  IconButton,
  Input,
  Select,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { Plus, Trash } from "@phosphor-icons/react";

const TARGET_TYPES = ["Emission reduction", "Energy efficiency", "Renewable energy", "Water reduction", "Waste reduction"];
const METRIC_UNITS = ["%", "tCO2e", "MWh", "ML", "tonnes"];
const TIMESCALES = ["by 2025", "by 2030", "by 2035", "by 2040", "by 2045", "by 2050"];

const EMPTY_TARGET = {
  target: "",
  type: "",
  metric: "",
  metricUnit: "%",
  timescale: "",
  sbti: false,
};

const TargetsTable = ({ targets = [], onChange, readOnly }) => {
  const updateTarget = (index, field, value) => {
    const updated = targets.map((t, i) => (i === index ? { ...t, [field]: value } : t));
    onChange(updated);
  };

  const addTarget = () => {
    onChange([...targets, { ...EMPTY_TARGET }]);
  };

  const removeTarget = (index) => {
    onChange(targets.filter((_, i) => i !== index));
  };

  return (
    <Box borderWidth="1px" borderColor="gray.200" borderRadius="xl" overflow="hidden" bg="white">
      <Table variant="simple" size="sm">
        <Thead bg="gray.50">
          <Tr>
            <Th>Target</Th>
            <Th>Type</Th>
            <Th>Metric</Th>
            <Th>Timescale</Th>
            <Th>SBTi</Th>
            {!readOnly && <Th w="40px" />}
          </Tr>
        </Thead>
        <Tbody>
          {targets.map((t, i) => (
            <Tr key={i}>
              <Td>
                <Input
                  size="sm"
                  value={t.target}
                  onChange={(e) => updateTarget(i, "target", e.target.value)}
                  isDisabled={readOnly}
                  placeholder="Enter target"
                />
              </Td>
              <Td>
                <Select
                  size="sm"
                  value={t.type}
                  onChange={(e) => updateTarget(i, "type", e.target.value)}
                  isDisabled={readOnly}
                  placeholder="Select type"
                >
                  {TARGET_TYPES.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </Select>
              </Td>
              <Td>
                <HStack spacing={1}>
                  <Input
                    size="sm"
                    type="number"
                    value={t.metric}
                    onChange={(e) => updateTarget(i, "metric", e.target.value)}
                    isDisabled={readOnly}
                    w="70px"
                  />
                  <Select
                    size="sm"
                    value={t.metricUnit}
                    onChange={(e) => updateTarget(i, "metricUnit", e.target.value)}
                    isDisabled={readOnly}
                    w="80px"
                  >
                    {METRIC_UNITS.map((unit) => (
                      <option key={unit} value={unit}>
                        {unit}
                      </option>
                    ))}
                  </Select>
                </HStack>
              </Td>
              <Td>
                <Select
                  size="sm"
                  value={t.timescale}
                  onChange={(e) => updateTarget(i, "timescale", e.target.value)}
                  isDisabled={readOnly}
                  placeholder="Select"
                >
                  {TIMESCALES.map((ts) => (
                    <option key={ts} value={ts}>
                      {ts}
                    </option>
                  ))}
                </Select>
              </Td>
              <Td>
                <Checkbox
                  isChecked={t.sbti}
                  onChange={(e) => updateTarget(i, "sbti", e.target.checked)}
                  isDisabled={readOnly}
                  colorScheme="green"
                />
              </Td>
              {!readOnly && (
                <Td>
                  <IconButton
                    aria-label="Remove target"
                    icon={<Trash size={16} />}
                    size="xs"
                    variant="ghost"
                    colorScheme="red"
                    onClick={() => removeTarget(i)}
                  />
                </Td>
              )}
            </Tr>
          ))}
        </Tbody>
      </Table>

      {!readOnly && (
        <Button
          leftIcon={<Plus size={16} />}
          variant="ghost"
          size="sm"
          w="100%"
          color="limeShade.600"
          onClick={addTarget}
          bg="lime.50"
          borderTopRadius={0}
        >
          <Text>Add target</Text>
        </Button>
      )}
    </Box>
  );
};

export default TargetsTable;
